import { useEffect, useRef, useState } from 'react';
import type { AppointmentFormState } from '../useAppointmentForm';

interface ContactFormProps {
  state: AppointmentFormState;
}

interface HCaptchaApi {
  render: (
    container: HTMLElement | string,
    params: { sitekey: string; callback?: (token: string) => void; 'expired-callback'?: () => void; theme?: 'light' | 'dark' },
  ) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

interface ReCaptchaApi {
  render: (
    container: HTMLElement | string,
    params: {
      sitekey: string;
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark';
      size?: 'normal' | 'compact' | 'invisible';
    },
  ) => number;
  reset: (id?: number) => void;
  ready?: (cb: () => void) => void;
}

declare global {
  interface Window {
    hcaptcha?: HCaptchaApi;
    grecaptcha?: ReCaptchaApi;
  }
}

export default function ContactForm({ state }: ContactFormProps) {
  const {
    config,
    customer,
    updateCustomer,
    honeypot,
    setHoneypot,
    setCaptchaToken,
    captchaError,
    setCaptchaError,
    registerCaptchaReset,
    resetCaptcha,
  } = state;
  const hcRef = useRef<HTMLDivElement | null>(null);
  const hcWidgetIdRef = useRef<string | null>(null);
  const grRef = useRef<HTMLDivElement | null>(null);
  const grWidgetIdRef = useRef<number | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Chiusura modal con tasto Esc
  useEffect(() => {
    if (!showPrivacyModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowPrivacyModal(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showPrivacyModal]);

  // Mobile virtual keyboard handling: keep the sticky "Conferma prenotazione"
  // CTA visible above the keyboard, and ensure the focused field is in view.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return;
    const update = () => {
      const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--aw-kb-offset', `${offset}px`);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.style.removeProperty('--aw-kb-offset');
    };
  }, []);

  useEffect(() => {
    const root = formRef.current;
    if (!root) return;
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;
      if (window.matchMedia('(min-width: 640px)').matches) return;
      window.setTimeout(() => {
        try {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch {
          target.scrollIntoView();
        }
      }, 300);
    };
    root.addEventListener('focusin', onFocusIn);
    return () => root.removeEventListener('focusin', onFocusIn);
  }, []);

  const hcaptchaEnabled = !!config?.antiAbuse.hcaptchaEnabled && !!config?.antiAbuse.hcaptchaSiteKey;
  const recaptchaEnabled = !!config?.antiAbuse.recaptchaEnabled && !!config?.antiAbuse.recaptchaSiteKey;
  const captchaThemeDark = config?.branding.defaultTheme === 'dark';

  // hCaptcha
  useEffect(() => {
    if (!hcaptchaEnabled) return;
    const SCRIPT_ID = 'hcaptcha-script';
    function render() {
      if (!hcRef.current || !window.hcaptcha || hcWidgetIdRef.current) return;
      hcWidgetIdRef.current = window.hcaptcha.render(hcRef.current, {
        sitekey: config!.antiAbuse.hcaptchaSiteKey!,
        callback: (token: string) => {
          setCaptchaToken(token);
          setCaptchaError(null);
        },
        'expired-callback': () => setCaptchaToken(undefined),
        theme: captchaThemeDark ? 'dark' : 'light',
      });
      registerCaptchaReset(() => {
        if (hcWidgetIdRef.current && window.hcaptcha?.reset) {
          try { window.hcaptcha.reset(hcWidgetIdRef.current); } catch { /* ignore */ }
        }
      });
    }
    if (window.hcaptcha) {
      render();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const sc = document.createElement('script');
      sc.id = SCRIPT_ID;
      sc.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      sc.async = true;
      sc.defer = true;
      sc.onload = render;
      document.head.appendChild(sc);
    } else {
      const t = window.setInterval(() => {
        if (window.hcaptcha) {
          window.clearInterval(t);
          render();
        }
      }, 200);
      return () => window.clearInterval(t);
    }
    return () => {
      if (hcWidgetIdRef.current && window.hcaptcha?.remove) {
        try { window.hcaptcha.remove(hcWidgetIdRef.current); } catch { /* ignore */ }
        hcWidgetIdRef.current = null;
      }
      registerCaptchaReset(null);
    };
  }, [hcaptchaEnabled, config, setCaptchaToken, setCaptchaError, registerCaptchaReset, captchaThemeDark]);

  // reCAPTCHA v2 checkbox
  useEffect(() => {
    if (!recaptchaEnabled) return;
    const SCRIPT_ID = 'recaptcha-script';
    function render() {
      if (!grRef.current || !window.grecaptcha || grWidgetIdRef.current !== null) return;
      try {
        grWidgetIdRef.current = window.grecaptcha.render(grRef.current, {
          sitekey: config!.antiAbuse.recaptchaSiteKey!,
          callback: (token: string) => {
            setCaptchaToken(token);
            setCaptchaError(null);
          },
          'expired-callback': () => setCaptchaToken(undefined),
          'error-callback': () => setCaptchaToken(undefined),
          theme: captchaThemeDark ? 'dark' : 'light',
        });
        registerCaptchaReset(() => {
          if (grWidgetIdRef.current !== null && window.grecaptcha?.reset) {
            try { window.grecaptcha.reset(grWidgetIdRef.current); } catch { /* ignore */ }
          }
        });
      } catch { /* ignore */ }
    }
    function whenReady() {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(render);
      } else {
        render();
      }
    }
    if (window.grecaptcha) {
      whenReady();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const sc = document.createElement('script');
      sc.id = SCRIPT_ID;
      sc.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      sc.async = true;
      sc.defer = true;
      sc.onload = whenReady;
      document.head.appendChild(sc);
    } else {
      const t = window.setInterval(() => {
        if (window.grecaptcha) {
          window.clearInterval(t);
          whenReady();
        }
      }, 200);
      return () => window.clearInterval(t);
    }
    return () => {
      // grecaptcha has no public "remove"; just clear our local widget ref
      // and detach the reset hook so the next mount re-renders cleanly.
      grWidgetIdRef.current = null;
      registerCaptchaReset(null);
    };
  }, [recaptchaEnabled, config, setCaptchaToken, setCaptchaError, registerCaptchaReset, captchaThemeDark]);

  if (!config) return null;

  return (
    <div ref={formRef}>
      <div className="aw-grid aw-grid-1 aw-grid-2">
        <div className="aw-field">
          <label className="aw-label" htmlFor="aw-first-name">Nome *</label>
          <input
            id="aw-first-name"
            className="aw-input"
            value={customer.firstName || ''}
            onChange={(e) => {
              const first = e.target.value;
              updateCustomer({ firstName: first, name: `${first} ${customer.lastName || ''}`.trim() });
            }}
            required
            autoComplete="given-name"
          />
        </div>
        <div className="aw-field">
          <label className="aw-label" htmlFor="aw-last-name">Cognome *</label>
          <input
            id="aw-last-name"
            className="aw-input"
            value={customer.lastName || ''}
            onChange={(e) => {
              const last = e.target.value;
              updateCustomer({ lastName: last, name: `${customer.firstName || ''} ${last}`.trim() });
            }}
            required
            autoComplete="family-name"
          />
        </div>
      </div>
      <div className="aw-grid aw-grid-1 aw-grid-2">
        <div className="aw-field">
          <label className="aw-label" htmlFor="aw-email">Email *</label>
          <input
            id="aw-email"
            type="email"
            className="aw-input"
            value={customer.email}
            onChange={(e) => updateCustomer({ email: e.target.value })}
            required
            autoComplete="email"
          />
        </div>
        <div className="aw-field">
          <label className="aw-label" htmlFor="aw-phone">Telefono *</label>
          <input
            id="aw-phone"
            type="tel"
            className="aw-input"
            value={customer.phone}
            onChange={(e) => updateCustomer({ phone: e.target.value })}
            required
            autoComplete="tel"
            inputMode="tel"
          />
        </div>
      </div>

      {/* Task #2786 — la selezione/visualizzazione sede è stata spostata in
          testa al flusso (StoreSelector nei layout), non più qui in fondo. */}

      {config.customFields?.map((f) => (
        <div className="aw-field" key={f.id}>
          <label className="aw-label" htmlFor={`aw-cf-${f.id}`}>
            {f.label}{f.required ? ' *' : ''}
          </label>
          {f.type === 'textarea' ? (
            <textarea
              id={`aw-cf-${f.id}`}
              className="aw-textarea"
              required={f.required}
              value={(customer.customAnswers?.[f.id] as string) || ''}
              onChange={(e) =>
                updateCustomer({
                  customAnswers: { ...(customer.customAnswers || {}), [f.id]: e.target.value },
                })
              }
            />
          ) : f.type === 'select' ? (
            <select
              id={`aw-cf-${f.id}`}
              className="aw-select"
              required={f.required}
              value={(customer.customAnswers?.[f.id] as string) || ''}
              onChange={(e) =>
                updateCustomer({
                  customAnswers: { ...(customer.customAnswers || {}), [f.id]: e.target.value },
                })
              }
            >
              <option value="">Seleziona...</option>
              {f.options?.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          ) : f.type === 'checkbox' ? (
            <label className="aw-checkbox-row">
              <input
                id={`aw-cf-${f.id}`}
                type="checkbox"
                checked={!!customer.customAnswers?.[f.id]}
                onChange={(e) =>
                  updateCustomer({
                    customAnswers: { ...(customer.customAnswers || {}), [f.id]: e.target.checked },
                  })
                }
              />
              <span>{f.label}</span>
            </label>
          ) : (
            <input
              id={`aw-cf-${f.id}`}
              type={f.type}
              className="aw-input"
              required={f.required}
              placeholder={f.placeholder}
              value={(customer.customAnswers?.[f.id] as string) || ''}
              onChange={(e) =>
                updateCustomer({
                  customAnswers: { ...(customer.customAnswers || {}), [f.id]: e.target.value },
                })
              }
            />
          )}
        </div>
      ))}

      {/* Sezione consensi GDPR — UX dedicata al form (allineata al chatbot AI):
          1) Lista checkbox consensi (privacy obbligatorio, marketing opzionale, eventuali custom)
          2) Link inline "Leggi l'informativa privacy completa" che apre un modal con il testo legale.
          Fallback ai due checkbox classici se il tenant non ha pubblicato un documento privacy. */}
      {config.gdpr?.consents && config.gdpr.consents.length > 0 ? (
        <div className="aw-privacy-block" style={{ marginTop: 12 }}>
          {config.gdpr.consents.map((c) => {
            const isPrivacy = c.key === 'privacy_policy' || c.key === 'privacy' || c.key === 'privacy_acceptance';
            const isMarketing = c.key === 'marketing';
            const checked = isPrivacy
              ? customer.privacyAccepted
              : isMarketing
              ? !!customer.marketingAccepted
              : !!customer.gdprConsents?.[c.key];
            const onChange = (val: boolean) => {
              if (isPrivacy) updateCustomer({ privacyAccepted: val });
              else if (isMarketing) updateCustomer({ marketingAccepted: val });
              else updateCustomer({ gdprConsents: { ...(customer.gdprConsents || {}), [c.key]: val } });
            };
            return (
              <label key={c.key} className="aw-checkbox-row" style={{ marginTop: 8, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onChange(e.target.checked)}
                  required={c.required}
                  style={{ marginTop: 2 }}
                />
                <span>
                  <span>{c.label}{c.required ? ' *' : ''}</span>
                  {c.description && (
                    <span className="aw-gdpr-help">{c.description}</span>
                  )}
                </span>
              </label>
            );
          })}

          {config.gdpr?.document && (
            <button
              type="button"
              className="aw-privacy-link"
              onClick={() => setShowPrivacyModal(true)}
              aria-haspopup="dialog"
              aria-expanded={showPrivacyModal}
            >
              <span aria-hidden="true" className="aw-privacy-link-icon">📋</span>
              <span>Leggi l'informativa privacy completa</span>
              <span className="aw-privacy-link-version">v{config.gdpr.document.version}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="aw-privacy-block" style={{ marginTop: 12 }}>
          <label className="aw-checkbox-row">
            <input
              type="checkbox"
              checked={customer.privacyAccepted}
              onChange={(e) => updateCustomer({ privacyAccepted: e.target.checked })}
              required
            />
            <span>{config.privacy.privacyText || 'Accetto l\'informativa sulla privacy *'}</span>
          </label>

          {config.privacy.marketingOptIn && (
            <label className="aw-checkbox-row" style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                checked={!!customer.marketingAccepted}
                onChange={(e) => updateCustomer({ marketingAccepted: e.target.checked })}
              />
              <span>Acconsento all'invio di comunicazioni commerciali</span>
            </label>
          )}

          {config.gdpr?.document && (
            <button
              type="button"
              className="aw-privacy-link"
              onClick={() => setShowPrivacyModal(true)}
              aria-haspopup="dialog"
              aria-expanded={showPrivacyModal}
            >
              <span aria-hidden="true" className="aw-privacy-link-icon">📋</span>
              <span>Leggi l'informativa privacy completa</span>
              <span className="aw-privacy-link-version">v{config.gdpr.document.version}</span>
            </button>
          )}
        </div>
      )}

      {/* Modal informativa privacy — apre il documento GDPR del tenant */}
      {showPrivacyModal && config.gdpr?.document && (
        <div
          className="aw-privacy-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aw-privacy-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPrivacyModal(false); }}
        >
          <div className="aw-privacy-modal">
            <div className="aw-privacy-modal-header">
              <div className="aw-privacy-modal-title-wrap">
                <span aria-hidden="true" className="aw-privacy-link-icon">🛡️</span>
                <h3 id="aw-privacy-modal-title" className="aw-privacy-modal-title">
                  {config.gdpr.document.title}
                </h3>
              </div>
              <button
                type="button"
                className="aw-privacy-modal-close"
                onClick={() => setShowPrivacyModal(false)}
                aria-label="Chiudi informativa privacy"
              >
                ✕
              </button>
            </div>
            <div className="aw-privacy-modal-body">
              {/* Render plain-text con paragrafi: niente HTML interpretato per
                  evitare qualunque rischio XSS sul widget pubblico. Eventuali
                  tag presenti nel content vengono visualizzati come testo. */}
              {config.gdpr.document.content.split(/\n{2,}/).map((para, i) => (
                <p key={i} style={{ margin: '0 0 10px 0', whiteSpace: 'pre-wrap' }}>{para}</p>
              ))}
            </div>
            <div className="aw-privacy-modal-footer">
              <span className="aw-privacy-modal-version">Versione {config.gdpr.document.version}</span>
              <button
                type="button"
                className="aw-btn aw-btn-primary"
                onClick={() => setShowPrivacyModal(false)}
              >
                Ho letto, chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {(hcaptchaEnabled || recaptchaEnabled) && (
        <div className="aw-field" style={{ marginTop: 12 }}>
          {hcaptchaEnabled && <div ref={hcRef} />}
          {recaptchaEnabled && <div ref={grRef} />}
          {captchaError && (
            <div
              className="aw-error"
              role="alert"
              style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <strong>
                {captchaError === 'missing'
                  ? 'Verifica captcha richiesta'
                  : 'Verifica captcha non riuscita'}
              </strong>
              <span style={{ fontSize: 13 }}>
                {captchaError === 'missing'
                  ? 'Completa la verifica "Non sono un robot" qui sopra prima di confermare la prenotazione.'
                  : 'Il token di verifica è scaduto o non è valido. Riprova la verifica e conferma di nuovo. Se il problema persiste, ricarica la pagina.'}
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="aw-btn aw-btn-ghost"
                  onClick={resetCaptcha}
                >
                  Riprova captcha
                </button>
                <button
                  type="button"
                  className="aw-btn aw-btn-ghost"
                  onClick={() => { if (typeof window !== 'undefined') window.location.reload(); }}
                >
                  Ricarica la pagina
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Honeypot */}
      <input
        type="text"
        className="aw-honeypot"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        name="website"
      />
    </div>
  );
}
